import { useEffect, useState } from 'react'
import { getDatabase, ref, push, onValue, query, orderByChild, limitToLast } from 'firebase/database'
import { getAuth } from 'firebase/auth'

// Default cap on how many of the most recent entries are fetched. The full
// ActivityLog stays in the database; the UI only loads (and subscribes to) the
// most recent `fetchLimit` entries so the page stays efficient as the log grows.
const DEFAULT_FETCH_LIMIT = 500

const useActivityLog = ({ fetchLimit = DEFAULT_FETCH_LIMIT } = {}) => {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const db = getDatabase()

  useEffect(() => {
    // Bound the subscription to the most recent entries by timestamp.
    const logsQuery = query(
      ref(db, 'ActivityLog'),
      orderByChild('timestamp'),
      limitToLast(fetchLimit)
    )
    const unsubscribe = onValue(logsQuery, (snapshot) => {
      const val = snapshot.val()
      if (!val) {
        setLogs([])
        setLoading(false)
        return
      }
      const list = Object.entries(val).map(([id, entry]) => ({
        id,
        ...entry
      }))
      list.sort((a, b) => b.timestamp - a.timestamp)
      setLogs(list)
      setLoading(false)
    })
    return () => unsubscribe()
  }, [db, fetchLimit])

  const logAction = async (action, details = {}) => {
    const auth = getAuth()
    const user = auth.currentUser
    if (!user) return
    const entry = {
      user: user.email || user.uid,
      timestamp: Date.now(),
      action,
      details
    }
    await push(ref(db, 'ActivityLog'), entry)
  }

  return { logs, loading, logAction }
}

export default useActivityLog
